/**
 * FairwayBid – Fehler benennen statt „Hoppla“
 *
 * Wenn das Einreichen eines Angebots fehlschlägt, stand bisher nur
 * „Hoppla, etwas ist schiefgelaufen“ auf dem Bildschirm. Für einen Händler
 * ist das eine Sackgasse, und für uns ist es keine Fehlermeldung, sondern
 * ein Rätsel: Es gibt keinen Hinweis darauf, ob die Angabe falsch war, das
 * Konto noch nicht freigeschaltet ist oder der Server nicht konnte.
 *
 * Dieses Modul holt aus dem Fehlerobjekt heraus, was tatsächlich passiert
 * ist, und übersetzt die Fälle, die wir kennen, in einen Satz, mit dem
 * jemand etwas anfangen kann. Alles Übrige wird wenigstens beim Namen
 * genannt, damit im Zweifel Code und Status im Support ankommen.
 *
 * Reine Zeichenkettenarbeit, kein React – damit prüfbar.
 */

/** Fälle, die wir zuordnen können, in Klartext. */
export const KNOWN_CODES = {
  'transaction-listing-not-found': 'Die Anfrage gibt es nicht mehr oder sie wurde geschlossen.',
  'transaction-invalid-transition':
    'Zu dieser Anfrage liegt von Ihnen bereits ein Angebot vor. Bearbeiten Sie es im Posteingang, statt ein zweites einzureichen.',
  'transaction-provider-not-found': 'Die ausschreibende Golfanlage ist nicht mehr erreichbar.',
  forbidden: 'Ihrem Konto fehlt die Berechtigung dafür.',
  'email-unverified': 'Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse.',
  'user-pending-approval': 'Ihr Konto wartet noch auf die Freischaltung.',
  'validation-invalid-params': 'Eine Angabe im Formular hat die Prüfung nicht bestanden.',
};

/**
 * Erster von der API gemeldeter Fehler, falls vorhanden.
 */
export const getApiError = error => {
  const list = error?.apiErrors;
  return Array.isArray(list) && list.length > 0 ? list[0] : null;
};

/**
 * Ein Satz zum Fehler – oder null, wenn nichts Brauchbares dabei ist.
 *
 * @param {Object} error Fehlerobjekt aus storableError
 * @returns {string|null}
 */
export const describeError = error => {
  if (!error) {
    return null;
  }

  const apiError = getApiError(error);
  const code = apiError?.code;
  if (code && KNOWN_CODES[code]) {
    return KNOWN_CODES[code];
  }

  // Der Server dieser Anwendung kann Angebote nur über einen vertrauens-
  // würdigen Zugang einreichen. Fehlt dessen Zugangsschlüssel in den
  // Umgebungsvariablen, scheitert jeder Versuch – ohne dass es am Angebot
  // liegt. Das kommt in der Entwicklung vor und soll benannt werden.
  // Bei 401/403 zaehlt der Text, den unser eigener Server gesetzt hat: Er
  // benennt die Ursache. Der Titel der API ist dann nur "Unauthorized" und
  // hilft niemandem weiter - er kommt erst danach zum Zug.
  const status = error?.status;
  if (status === 401 || status === 403) {
    const reason = error?.statusText || apiError?.title;
    const suffix = code ? ` [${code}]` : '';
    return reason
      ? `${reason}${suffix}`
      : `Der Server hat den Vorgang abgelehnt (${status}).${suffix}`;
  }

  // Der Server dieser Anwendung antwortete frueher im Fehlerfall mit einem
  // Objekt, aus dem der Browser nur das Feld "error" lesen konnte. Aeltere
  // Antworten dieser Form sollen trotzdem etwas anzeigen.
  const parts = [];
  if (apiError?.title) parts.push(apiError.title);
  else if (error?.statusText) parts.push(error.statusText);
  else if (error?.error) parts.push(error.error);
  else if (error?.message) parts.push(error.message);
  if (status) parts.push(`Status ${status}`);
  if (code) parts.push(code);

  if (parts.length > 0) {
    return parts.join(' · ');
  }

  // Letzte Rettung: lieber der blanke Name des Fehlers als gar nichts. Ein
  // Fehler ohne jede Angabe ist fuer den Haendler eine Sackgasse und fuer den
  // Support wertlos.
  return error?.name ? `Fehler: ${error.name}` : null;
};
